// @file store for plans (pricing, quotas) and user actions to update them
import type { AdvertisedQuotasResult, EstimateSwitchingCostResult, User } from '@@/types'
import type { PlansState, UpgradeSource, UpgradeStep } from '@@/vuexstore/modules/plans'
import { blankState } from '@@/vuexstore/modules/plans'
import { defineStore } from 'pinia'
import type { ComputedRef } from 'vue'
import { computed, ref } from 'vue'
import type { Store } from 'vuex'

export const usePersonalPlansStore = defineStore('personalPlans', () => {
  const vuexStore = ref<Store<any> | undefined>(undefined)

  const { plans, plansFetched, fetchPlans } = usePlans()
  const { advertisedQuotas, isLoadingAdvertisedQuotas, fetchAdvertisedQuotas } = useQuotas()

  // The state here is used by a root component and getters would be called before the vuex store is available on the window object.
  // We would need to rely on this method to populate it instead of directly called getVuexStore.
  const initialize = (options: { vuexStore: Store<any> | undefined }): void => {
    vuexStore.value = options.vuexStore
  }

  function usePlans(): {
    plans: ComputedRef<PlansState['plans']>
    plansFetched: ComputedRef<boolean>
    fetchPlans: () => void
  } {
    const plans = computed<PlansState['plans']>(() => vuexStore.value?.getters['plans/plans'] ?? blankState().plans)

    const plansFetched = computed<boolean>(() => vuexStore.value?.getters['plans/plansFetched'] ?? false)

    const fetchPlans = (): void => {
      void vuexStore.value?.dispatch('plans/fetchPlans')
    }
    return { plans, plansFetched, fetchPlans }
  }

  function useQuotas(): {
    advertisedQuotas: ComputedRef<AdvertisedQuotasResult>
    isLoadingAdvertisedQuotas: ComputedRef<boolean>
    fetchAdvertisedQuotas: () => void
  } {
    const advertisedQuotas = computed<AdvertisedQuotasResult>(
      () => vuexStore.value?.getters['plans/advertisedQuotas'] ?? blankState().advertisedQuotas,
    )

    const isLoadingAdvertisedQuotas = computed<boolean>(
      () => vuexStore.value?.getters['plans/isLoadingAdvertisedQuotas'] ?? false,
    )

    const fetchAdvertisedQuotas = (): void => {
      void vuexStore.value?.dispatch('plans/fetchAdvertisedQuotas')
    }

    return { advertisedQuotas, isLoadingAdvertisedQuotas, fetchAdvertisedQuotas }
  }

  // Plan upgrades
  const estimatedCancellationRefundAmount = computed<string | null>(
    () => vuexStore.value?.getters['plans/estimatedCancellationRefundAmount'] ?? null,
  )
  const estimatedUpgradeCost = computed<EstimateSwitchingCostResult | null>(
    () => vuexStore.value?.getters['plans/estimatedUpgradeCost'] ?? null,
  )
  const hasUserTriggeredUpgrade = computed<boolean>(
    () => vuexStore.value?.getters['plans/hasUserTriggeredUpgrade'] ?? false,
  )
  const isLoadingCancellationRefundInformation = computed<boolean>(
    () => vuexStore.value?.getters['plans/isLoadingCancellationRefundInformation'] ?? false,
  )
  const isProcessingUpgrade = computed<boolean>(() => vuexStore.value?.getters['plans/isProcessingUpgrade'] ?? false)
  const upgradeError = computed(() => vuexStore.value?.getters['plans/upgradeError'] ?? '')
  const upgradeSource = computed<UpgradeSource | null>(() => vuexStore.value?.getters['plans/upgradeSource'] ?? null)
  const upgradeStep = computed<UpgradeStep | null>(() => vuexStore.value?.getters['plans/upgradeStep'] ?? null)
  const xUpgradeModal = computed<boolean>(() => vuexStore.value?.getters['plans/xUpgradeModal'] ?? false)

  const closePlanUpgradeModal = (): void => {
    void vuexStore.value?.dispatch('plans/closePlanUpgradeModal')
  }

  const setPlanUpgradeSource = (upgradeSource: UpgradeSource): void => {
    void vuexStore.value?.dispatch('plans/setUpgradeSource', { upgradeSource })
  }

  function showUpgradeGoldPlanModal(payload: { paymentSchedule: string; userPlanCurrency: string }): void {
    void vuexStore.value?.dispatch('plans/showUpgradeGoldPlanModal', payload)
  }

  function showPlanUpgradeToGoldModal(payload: { upgradeSource?: UpgradeSource }): void {
    void vuexStore.value?.dispatch('plans/showPlanUpgradeToGoldModal', payload)
  }

  function showPlanUpgradeToGoldModalCheckingQuota(payload: { tierQuota: number; wallsUsed: number }): void {
    void vuexStore.value?.dispatch('plans/showPlanUpgradeToGoldModalCheckingQuota', payload)
  }

  function showUpgradePlatinumPlanModal(payload: { paymentSchedule: string; userPlanCurrency: string }): void {
    void vuexStore.value?.dispatch('plans/showUpgradePlatinumPlanModal', payload)
  }

  function showUpgradeToPlatinumModal(payload: {
    membershipTier: string
    paymentSchedule?: string
    userPlanCurrency?: string | null
  }): void {
    void vuexStore.value?.dispatch('plans/showUpgradeToPlatinumModal', payload)
  }

  function showUpgradeToPlatinumModalCheckingScheduledChanges({
    membershipTier,
    paymentSchedule,
  }: {
    membershipTier: string
    paymentSchedule?: string
  }): void {
    void vuexStore.value?.dispatch('plans/showUpgradeToPlatinumModalCheckingScheduledChanges', {
      membershipTier,
      paymentSchedule,
    })
  }

  function upgradeToGold({ paymentSchedule }: { paymentSchedule: string }): void {
    void vuexStore.value?.dispatch('plans/upgradeToGold', { paymentSchedule })
  }

  function upgradeToGoldDifferentCurrency({ paymentSchedule }: { paymentSchedule: string }): void {
    void vuexStore.value?.dispatch('plans/upgradeToGoldDifferentCurrency', { paymentSchedule })
  }

  function upgradeToPlatinum({ paymentSchedule }: { paymentSchedule: string }): void {
    void vuexStore.value?.dispatch('plans/upgradeToPlatinum', { paymentSchedule })
  }

  function upgradeToPlatinumDifferentCurrency({ paymentSchedule }: { paymentSchedule: string }): void {
    void vuexStore.value?.dispatch('plans/upgradeToPlatinumDifferentCurrency', { paymentSchedule })
  }

  const userTriggeredUpgrade = (payload: { user: User; upgradeSource: UpgradeSource }): void => {
    void vuexStore.value?.dispatch('plans/userTriggeredUpgrade', payload, { root: true })
  }

  // Plan details, normalized for display
  const displayedEstimatedUpgradeCost = computed<EstimateSwitchingCostResult>(() => {
    if (estimatedUpgradeCost.value == null) {
      return {
        is_refund: false,
        currency_code: '',
        new_plan_cost: '',
        unused_portion: '',
        refund_amount: '',
        amount_due: '',
        invoice_amount: '',
      }
    }
    return estimatedUpgradeCost.value
  })

  const displayedQuotas = computed(() => {
    function formatNullableNumber(value: number | null): string {
      if (value == null) return ''
      return String(value)
    }

    return {
      platinum: {
        max_walls: advertisedQuotas.value.platinum.max_walls ?? 0,
        max_upload_megabytes: formatNullableNumber(advertisedQuotas.value.platinum.max_upload_megabytes),
      },
      gold: {
        max_walls: advertisedQuotas.value.gold.max_walls ?? 0,
        max_upload_megabytes: formatNullableNumber(advertisedQuotas.value.gold.max_upload_megabytes),
      },
      neon: {
        max_walls: advertisedQuotas.value.neon.max_walls ?? 0,
        max_upload_megabytes: formatNullableNumber(advertisedQuotas.value.neon.max_upload_megabytes),
      },
    }
  })

  const displayedPrices = computed(() => {
    return {
      platinum: {
        monthly: plans.value.platinum.monthly.formattedPrice ?? '',
        annual: plans.value.platinum.annual.formattedPrice ?? '',
      },
      gold: {
        monthly: plans.value.gold.monthly.formattedPrice ?? '',
        annual: plans.value.gold.annual.formattedPrice ?? '',
      },
    }
  })

  return {
    initialize,

    // getters
    advertisedQuotas,
    displayedEstimatedUpgradeCost,
    displayedPrices,
    displayedQuotas,
    estimatedCancellationRefundAmount,
    estimatedUpgradeCost,
    hasUserTriggeredUpgrade,
    isLoadingAdvertisedQuotas,
    isLoadingCancellationRefundInformation,
    isProcessingUpgrade,
    plans,
    plansFetched,
    upgradeError,
    upgradeSource,
    upgradeStep,
    xUpgradeModal,

    // actions
    closePlanUpgradeModal,
    fetchAdvertisedQuotas,
    fetchPlans,
    setPlanUpgradeSource,
    showPlanUpgradeToGoldModal,
    showPlanUpgradeToGoldModalCheckingQuota,
    showUpgradeGoldPlanModal,
    showUpgradePlatinumPlanModal,
    showUpgradeToPlatinumModal,
    showUpgradeToPlatinumModalCheckingScheduledChanges,
    upgradeToGold,
    upgradeToGoldDifferentCurrency,
    upgradeToPlatinum,
    upgradeToPlatinumDifferentCurrency,
    userTriggeredUpgrade,
  }
})
